/**
 * AppContext - Global application state management
 * Manages notifications, user preferences, and UI state
 */

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
  FC,
} from 'react';

// Notification types
export interface Notification {
  id: string;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
  autoHideDuration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// App state types
export interface AppState {
  notifications: Notification[];
  sidebarCollapsed: boolean;
  userPreferences: {
    theme: 'light' | 'dark' | 'system';
    compactMode: boolean;
  };
  isOffline: boolean;
}

// Action types
type AppAction =
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_NOTIFICATIONS' }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR_COLLAPSED'; payload: boolean }
  | { type: 'SET_THEME'; payload: 'light' | 'dark' | 'system' }
  | { type: 'SET_COMPACT_MODE'; payload: boolean }
  | { type: 'SET_OFFLINE'; payload: boolean };

// Initial state
const initialState: AppState = {
  notifications: [],
  sidebarCollapsed: false,
  userPreferences: {
    theme: 'light',
    compactMode: false,
  },
  isOffline: false,
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [...state.notifications, action.payload],
      };
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter((n) => n.id !== action.payload),
      };
    case 'CLEAR_NOTIFICATIONS':
      return {
        ...state,
        notifications: [],
      };
    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        sidebarCollapsed: !state.sidebarCollapsed,
      };
    case 'SET_SIDEBAR_COLLAPSED':
      return {
        ...state,
        sidebarCollapsed: action.payload,
      };
    case 'SET_THEME':
      return {
        ...state,
        userPreferences: {
          ...state.userPreferences,
          theme: action.payload,
        },
      };
    case 'SET_COMPACT_MODE':
      return {
        ...state,
        userPreferences: {
          ...state.userPreferences,
          compactMode: action.payload,
        },
      };
    case 'SET_OFFLINE':
      return {
        ...state,
        isOffline: action.payload,
      };
    default:
      return state;
  }
}

// Context type
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Convenience methods
  notify: (
    message: string,
    severity?: Notification['severity'],
    options?: Partial<Omit<Notification, 'id' | 'message' | 'severity'>>
  ) => void;
  notifySuccess: (message: string) => void;
  notifyError: (message: string, retryFn?: () => void) => void;
  notifyWarning: (message: string) => void;
  notifyInfo: (message: string) => void;
  dismissNotification: (id: string) => void;
  toggleSidebar: () => void;
}

// Create context
const AppContext = createContext<AppContextType | null>(null);

// Generate unique ID for notifications
let notificationId = 0;
const generateId = () => `notification-${++notificationId}`;

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  const notify = useCallback(
    (
      message: string,
      severity: Notification['severity'] = 'info',
      options?: Partial<Omit<Notification, 'id' | 'message' | 'severity'>>
    ) => {
      const notification: Notification = {
        id: generateId(),
        message,
        severity,
        autoHideDuration: severity === 'error' ? undefined : 5000,
        ...options,
      };
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
    },
    []
  );

  const notifySuccess = useCallback(
    (message: string) => notify(message, 'success'),
    [notify]
  );

  const notifyError = useCallback(
    (message: string, retryFn?: () => void) =>
      notify(message, 'error', retryFn ? { action: { label: 'Retry', onClick: retryFn } } : undefined),
    [notify]
  );

  const notifyWarning = useCallback(
    (message: string) => notify(message, 'warning'),
    [notify]
  );

  const notifyInfo = useCallback(
    (message: string) => notify(message, 'info'),
    [notify]
  );

  const dismissNotification = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
  }, []);

  const toggleSidebar = useCallback(() => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  }, []);

  const value: AppContextType = {
    state,
    dispatch,
    notify,
    notifySuccess,
    notifyError,
    notifyWarning,
    notifyInfo,
    dismissNotification,
    toggleSidebar,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Hook to use app context
export function useApp(): AppContextType {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

// Selector hooks for specific state slices
export function useNotifications(): Notification[] {
  const { state } = useApp();
  return state.notifications;
}

export function useSidebarCollapsed(): boolean {
  const { state } = useApp();
  return state.sidebarCollapsed;
}

export function useUserPreferences(): AppState['userPreferences'] {
  const { state } = useApp();
  return state.userPreferences;
}

export function useIsOffline(): boolean {
  const { state } = useApp();
  return state.isOffline;
}
